if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.mql = window.matchMedia('(min-width: 750px)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.boundOnSlideChanged = debounce(this.onSlideChanged.bind(this), 200);
      this.boundOnDesktopLayoutChange = this.onDesktopLayoutChange.bind(this);
      this.boundThumbnailHandlers = [];
      this.liveRegionTimer = null;
      this.stickyHeader = null;
    }

    connectedCallback() {
      this.cacheElements();
      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', this.boundOnSlideChanged);
      this.bindThumbnailButtons();
      this.onDesktopLayoutChange();

      if (typeof this.mql.addEventListener === 'function') {
        this.mql.addEventListener('change', this.boundOnDesktopLayoutChange);
      } else if (typeof this.mql.addListener === 'function') {
        this.mql.addListener(this.boundOnDesktopLayoutChange);
      }
    }

    disconnectedCallback() {
      if (this.elements.viewer) {
        this.elements.viewer.removeEventListener('slideChanged', this.boundOnSlideChanged);
      }

      this.unbindThumbnailButtons();
      this.clearLiveRegionTimer();

      if (typeof this.mql.removeEventListener === 'function') {
        this.mql.removeEventListener('change', this.boundOnDesktopLayoutChange);
      } else if (typeof this.mql.removeListener === 'function') {
        this.mql.removeListener(this.boundOnDesktopLayoutChange);
      }
    }

    cacheElements() {
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
      };
    }

    bindThumbnailButtons() {
      this.unbindThumbnailButtons();
      if (!this.elements.thumbnails) return;

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');
        if (!button) return;

        const handler = this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false);
        button.addEventListener('click', handler);
        this.boundThumbnailHandlers.push({ button, handler });
      });
    }

    unbindThumbnailButtons() {
      if (!this.boundThumbnailHandlers.length) return;

      this.boundThumbnailHandlers.forEach(({ button, handler }) => {
        button.removeEventListener('click', handler);
      });

      this.boundThumbnailHandlers = [];
    }

    onDesktopLayoutChange() {
      if (!this.elements.viewer || !this.dataset.desktopLayout?.includes('thumbnail')) return;

      if (this.mql.matches) {
        this.removeListSemantic();
      } else {
        this.restoreListSemantic();
      }
    }

    onSlideChanged(event) {
      const currentMediaId = event?.detail?.currentElement?.dataset?.mediaId;
      if (!currentMediaId || !this.elements.thumbnails) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentMediaId}"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend = false) {
      if (!this.elements.viewer) return;

      const activeMedia =
        this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
        this.elements.viewer.querySelector('[data-media-id]');

      if (!activeMedia) return;

      const mediaItems = this.elements.viewer.querySelectorAll('[data-media-id]');
      mediaItems.forEach((element) => element.classList.remove('is-active'));
      activeMedia.classList.add('is-active');

      if (prepend) {
        const mediaParent = activeMedia.parentElement;
        if (mediaParent && mediaParent.firstElementChild !== activeMedia) {
          mediaParent.prepend(activeMedia);
        }

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail?.parentElement?.firstElementChild !== activeThumbnail) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        if (typeof this.elements.viewer.slider?.resetPages === 'function') {
          this.elements.viewer.slider.resetPages();
        }
      }

      this.preventStickyHeader();

      window.requestAnimationFrame(() => {
        if (!this.mql.matches || this.elements.thumbnails) {
          activeMedia.parentElement?.scrollTo({
            left: activeMedia.offsetLeft,
            behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
          });
        }

        const activeMediaRect = activeMedia.getBoundingClientRect();
        if (activeMediaRect.top <= -0.5) {
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: this.reduceMotion.matches ? 'auto' : 'smooth' });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      if (!activeThumbnail) return;

      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails
        .querySelectorAll('button')
        .forEach((element) => element.removeAttribute('aria-current'));

      thumbnail.querySelector('button')?.setAttribute('aria-current', 'true');

      const sliderApi = this.elements.thumbnails.slider;
      if (!sliderApi || typeof this.elements.thumbnails.isSlideVisible !== 'function') return;
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      sliderApi.scrollTo({
        left: thumbnail.offsetLeft,
        behavior: this.reduceMotion.matches ? 'auto' : 'smooth',
      });
    }

    announceLiveRegion(activeItem, position) {
      if (!this.elements.liveRegion || !position) return;

      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;

      const announce = () => {
        this.clearLiveRegionTimer();
        this.elements.liveRegion.setAttribute('aria-hidden', 'false');
        this.elements.liveRegion.textContent = window.accessibilityStrings.imageAvailable.replace('[index]', position);
        this.liveRegionTimer = window.setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'true');
        }, 2000);
      };

      if (image.complete) {
        announce();
        return;
      }

      image.addEventListener('load', announce, { once: true });
    }

    clearLiveRegionTimer() {
      if (this.liveRegionTimer) {
        window.clearTimeout(this.liveRegionTimer);
        this.liveRegionTimer = null;
      }
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }

      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      }
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;

      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer?.slider) return;

      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems?.forEach((slide) => {
        slide.setAttribute('role', 'presentation');
      });
    }

    restoreListSemantic() {
      if (!this.elements.viewer?.slider) return;

      this.elements.viewer.slider.removeAttribute('role');
      this.elements.viewer.sliderItems?.forEach((slide) => {
        slide.removeAttribute('role');
      });
    }
  }

  customElements.define('media-gallery', MediaGallery);
}